import React, { useEffect } from 'react'

import styled from 'styled-components'

import { InfluencerRegisterEnum } from 'src/Types/users'
import TiktokIcon from 'src/Assets/Svg/tiktok.svg?react'
import TiktokIconLight from 'src/Assets/Svg/tiktok_light.svg?react'
import { PremiumCheckbox } from 'src/Components/PremiumCheckbox'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import LockIcon from 'src/Assets/Svg/pages-icons/LockMini.svg?react'
import { APP } from 'src/Configs/App'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useThemeMode } from 'src/Hooks/useThemeMode'
import {
  creatorRegAuthorizationFailedEvent,
  creatorRegAuthorizationStartedEvent,
  creatorRegStartEvent,
} from 'src/Helpers/TagManager/CreatorsEvents'

import { ErrorReflection } from '../LoginCreatorError/ErrorReflection'

const Headline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  width: 100%;
`

const HeadlineTitle = styled.div`
  font-family: 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: ${({ theme }) => theme.color.darkGrey};
  width: 100%;
  line-height: 1.5;
  text-align: center;
`

const HeadlineDesc = styled(HeadlineTitle)`
  font-weight: 400;
  font-size: 14px;
`

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const CheckboxLabel = styled(HeadlineDesc)`
  text-align: left;
  max-width: 100%;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`

const ContentReq = styled.a`
  color: ${({ theme }) => theme.color.brandGreen};
`

const ButtonBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  min-height: 74px;
  width: 100%;
`

const SecureRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

const SecureText = styled.div`
  font-family: 'Segoe UI', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: left;
  white-space: nowrap;
`
const StyledPremiumCheckbox = styled(PremiumCheckbox)`
  padding: 0px;
  gap: 8px;
  flex-shrink: 1;
`
const StyledLockIcon = styled(LockIcon)`
  color: ${({ theme }) => theme.color.darkGrey};
`

interface LoginCreatorProps {
  status: InfluencerRegisterEnum
}

export const LoginCreator: React.FC<LoginCreatorProps> = ({ status }) => {
  useEffect(() => {
    status === InfluencerRegisterEnum.JOIN && creatorRegStartEvent()

    status === InfluencerRegisterEnum.ERROR &&
      creatorRegAuthorizationFailedEvent()
  }, [status])

  const { resolvedTheme } = useThemeMode()
  const TikTok = resolvedTheme === 'dark' ? TiktokIconLight : TiktokIcon

  const [checked, setChecked] = React.useState(false)
  const [isConnecting, setIsConnecting] = React.useState(false)
  const { search } = useCustomSearchParams()
  const reason = search.get('reason') || ''
  if (
    status === InfluencerRegisterEnum.JOIN ||
    status === InfluencerRegisterEnum.ERROR
  ) {
    const handleConnectClick = (): void => {
      setIsConnecting(true)
      creatorRegAuthorizationStartedEvent()
      window.location.href = APP.TIKTOK_AUTH_API
    }
    const isDisabled = !checked || isConnecting
    return (
      <>
        <Headline>
          <HeadlineTitle>
            Earn money by using music in your TikTok videos
          </HeadlineTitle>
          <HeadlineDesc>
            We match creators with trending music and pay based on your video
            performance
          </HeadlineDesc>
        </Headline>
        <CheckboxRow>
          <StyledPremiumCheckbox
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          >
            <CheckboxLabel>
              <span>I`ve read and agree to the </span>
              <ContentReq
                target='_blank'
                href='https://help.soundcamps.com/en/articles/9757440-profile-requirements'
              >
                content requirements
              </ContentReq>
            </CheckboxLabel>
          </StyledPremiumCheckbox>
        </CheckboxRow>
        {reason && <ErrorReflection text={reason} />}
        <ButtonBlock>
          <CTAButton
            disabled={isDisabled}
            onClick={handleConnectClick}
            icon={<TikTok width={16} height={16} />}
          >
            {isConnecting ? 'Connecting...' : 'Connect your TikTok'}
          </CTAButton>
          <SecureRow>
            <StyledLockIcon />
            <SecureText>This connection is secure</SecureText>
          </SecureRow>
        </ButtonBlock>
      </>
    )
  }
  return null
}
